import React from 'react';
import { twMerge } from 'tailwind-merge';
import { applyTypography, BaseButton } from '@repo/common';

import { ReactComponent as BackpackOutlineIcon } from '@repo/common/assets/icons/32px/icon_32px_backpack_outline.svg';

const DEFAULT_LABELS = {
  title: 'Option',
  caption: 'caption',
};

const isLarge = (size) => size === 'large';
const isSelected = (state) => state === 'selected';
const isSaved = (state) => state === 'saved';
const hasIcon = (iconPosition) => iconPosition !== 'none';

const containerClassesByVariant = ({
  size,
  state,
  iconPosition,
  placeholder,
}) => {
  const baseClasses =
    'relative flex overflow-hidden rounded-[15px] border-2 border-transparent transition-[background-color,border-color,box-shadow,opacity] duration-300 ease-out focus-visible:outline-2 focus-visible:outline-green-deep03 focus-visible:outline-offset-2';
  const interactiveClasses =
    placeholder || isSelected(state) || isSaved(state)
      ? ''
      : 'hover:border-surface-green40 hover:bg-green-light01 hover:opacity-100 hover:shadow-[0_10px_30px_rgba(0,158,79,0.08)] active:border-surface-green40 active:bg-green-light01 active:opacity-100 active:shadow-[0_10px_30px_rgba(0,158,79,0.08)]';
  const surfaceClasses = placeholder
    ? 'bg-surface-lightgray60 opacity-[0.38] shadow-[0_0_0_rgba(0,0,0,0)]'
    : isSelected(state)
    ? 'border-surface-green40 bg-green-light01 opacity-100 shadow-[0_10px_30px_rgba(0,158,79,0.08)]'
    : 'bg-surface-lightgray60 opacity-[0.94] shadow-[0_0_0_rgba(0,0,0,0)]';

  if (isLarge(size)) {
    return twMerge(
      baseClasses,
      'w-full max-w-[278px] flex-col items-center gap-2 p-6',
      interactiveClasses,
      surfaceClasses
    );
  }

  if (iconPosition === 'none') {
    return twMerge(
      baseClasses,
      'min-h-[76px] w-full items-center justify-center px-6 py-4',
      interactiveClasses,
      surfaceClasses
    );
  }

  return twMerge(
    baseClasses,
    'min-h-[76px] w-full items-center gap-2 p-4',
    interactiveClasses,
    surfaceClasses
  );
};

const iconBadgeClasses = ({ size, state, iconPosition, placeholder }) => {
  const isPlainSmallLeft = size === 'small' && iconPosition === 'left';

  return twMerge(
    'flex shrink-0 items-center justify-center rounded-[8px] text-current transition-[background-color,opacity] duration-300 ease-out',
    isLarge(size) ? 'p-2' : 'h-full px-2',
    placeholder
      ? 'bg-transparent opacity-0'
      : isPlainSmallLeft
      ? 'bg-transparent opacity-100 group-hover:bg-transparent group-active:bg-transparent'
      : isSelected(state)
      ? 'bg-surface-green10 opacity-100'
      : 'bg-[rgba(236,233,228,0.5)] opacity-[0.82] group-hover:bg-surface-green10 group-hover:opacity-100 group-active:bg-surface-green10 group-active:opacity-100'
  );
};

const contentClasses = ({ size, iconPosition }) =>
  twMerge(
    'flex min-w-0 flex-col gap-[2px] transition-opacity duration-300 ease-out',
    isLarge(size)
      ? 'w-full items-center text-center'
      : iconPosition === 'none'
      ? 'w-full items-center text-center'
      : 'flex-1 items-start text-left'
  );

const titleClasses = ({ title, size, state, placeholder }) => {
  if (placeholder) {
    return twMerge(applyTypography('b16Strong', title), 'opacity-0');
  }

  if (isSelected(state) || isSaved(state)) {
    return twMerge(
      applyTypography('b16Strong', title),
      'text-green-deep02 transition-colors duration-300 ease-out'
    );
  }

  return twMerge(
    applyTypography('b16Strong', title),
    'transition-colors duration-300 ease-out group-hover:text-green-deep02 group-active:text-green-deep02',
    isLarge(size) ? 'text-text-tertiary' : 'text-text-secondary'
  );
};

const captionClasses = ({ caption, state, placeholder }) =>
  twMerge(
    applyTypography('b13', caption),
    placeholder ? 'opacity-0' : '',
    'transition-colors duration-300 ease-out group-hover:text-green-deep02 group-active:text-green-deep02',
    isSelected(state) || isSaved(state)
      ? 'text-green-deep02'
      : 'text-text-placeholder'
  );

const renderIconNode = (iconNode, iconClassName) => {
  if (!React.isValidElement(iconNode)) return iconNode;

  return React.cloneElement(iconNode, {
    'aria-hidden': iconNode.props['aria-hidden'] ?? true,
    className: twMerge(iconNode.props.className, iconClassName),
  });
};

/**
 * @example
 * <PathfinderSelectBar
 *   title="Option"
 *   caption="option"
 *   size="small"
 *   state="selected"
 *   iconPosition="left"
 *   onClick={handleSelect}
 * />
 */

/**
 * PathfinderSelectBar 재사용 컴포넌트
 *
 * @component
 * @param {string} title 카드의 메인 텍스트
 * @param {string} caption 카드의 보조 텍스트
 * @param {"small" | "large"} size Figma 기준 small / large 변형
 * @param {"default" | "selected" | "saved"} state 선택 상태
 * @param {"left" | "right" | "none"} iconPosition 아이콘 배치 위치
 * @param {React.ReactNode} icon 기본 상태에서 사용할 아이콘
 * @param {React.ReactNode} selectedIcon selected 상태에서 사용할 아이콘
 * @param {string} className 외부에서 덮어쓸 Tailwind className
 * @param {("button" | "submit" | "reset")} type 버튼 type
 *
 */
function PathfinderSelectBar({
  title = DEFAULT_LABELS.title,
  caption = DEFAULT_LABELS.caption,
  size = 'small',
  state = 'default',
  iconPosition = 'left',
  icon = <BackpackOutlineIcon />,
  selectedIcon,
  placeholder = false,
  className = '',
  type = 'button',
  ...props
}) {
  const shouldRenderIcon = hasIcon(iconPosition);
  const resolvedSelectedIcon = selectedIcon ?? icon;

  const iconElement = shouldRenderIcon ? (
    <span
      className={iconBadgeClasses({ size, state, iconPosition, placeholder })}
    >
      <span className='relative size-8' aria-hidden>
        {renderIconNode(
          icon,
          twMerge(
            'absolute inset-0 size-8 transition-[color,opacity] duration-300 ease-out group-hover:text-green-deep02 group-active:text-green-deep02',
            placeholder
              ? 'opacity-0'
              : isSelected(state)
              ? 'opacity-0 text-green-deep02'
              : 'opacity-100 text-text-placeholder'
          )
        )}
        {renderIconNode(
          resolvedSelectedIcon,
          twMerge(
            'absolute inset-0 size-8 transition-[color,opacity] duration-300 ease-out',
            placeholder
              ? 'opacity-0'
              : isSelected(state)
              ? 'opacity-100 text-green-deep02'
              : 'opacity-0 text-green-deep02'
          )
        )}
      </span>
    </span>
  ) : null;

  const showIconBefore =
    shouldRenderIcon && (isLarge(size) || iconPosition === 'left');
  const showIconAfter =
    shouldRenderIcon && !isLarge(size) && iconPosition === 'right';

  return (
    <BaseButton
      type='text'
      size='m'
      htmlType={type}
      typo={false}
      isStatic
      icon={showIconBefore || showIconAfter ? iconElement : null}
      iconPosition={showIconAfter ? 'after' : 'before'}
      gap='gap-2'
      className={twMerge(
        'h-auto min-w-0 p-0',
        containerClassesByVariant({ size, state, iconPosition, placeholder }),
        'group',
        isLarge(size)
          ? '[&>span]:flex-col [&>span]:items-center'
          : '[&>span]:h-full',
        placeholder ? 'cursor-default pointer-events-none' : 'cursor-pointer',
        className
      )}
      textWrapperClass={twMerge(
        'h-auto',
        isLarge(size) || iconPosition === 'none' ? 'flex-none' : '',
        contentClasses({ size, iconPosition })
      )}
      {...props}
    >
      <span className={titleClasses({ title, size, state, placeholder })}>
        {title}
      </span>
      <span className={captionClasses({ caption, state, placeholder })}>
        {caption}
      </span>
    </BaseButton>
  );
}

export default PathfinderSelectBar;
